import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router';
import { AssetUrls } from 'config/assets';
import { showError, showSuccess } from 'utils/toastNotifications';
import { useVerifyEmailChangeQuery } from 'store/services/auth/authApi';
import './EmailVerificationPage.css';

const EmailVerificationPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [countdown, setCountdown] = useState(5);

  const { data: verificationData, isLoading, error } = useVerifyEmailChangeQuery(token || '', {
    skip: !token,
  });

  // Handle successful verification
  useEffect(() => {
    if (verificationData?.message) {
      showSuccess(verificationData.message);

      // Start countdown for redirect
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            navigate('/welcome');
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [verificationData, navigate]);

  // Handle error cases
  useEffect(() => {
    if (error) {
      const errorMessage = (error as any)?.data?.message || 'This verification link is invalid or has expired.';
      showError(errorMessage);

      // Redirect to login after showing error
      setTimeout(() => navigate('/welcome'), 5000);
    }
  }, [error, navigate]);

  if (isLoading) {
    return (
      <div className="email-verification-page">
        <div className="email-verification-container">
          <div className="email-verification-loading">
            <div className="email-verification-spinner"></div>
            <p>Verifying your email change...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="email-verification-page">
        <div className="email-verification-container">
          <div className="email-verification-logo">
            <img src={AssetUrls.logos.iconLogo} alt="TradeMagic" />
          </div>

          <div className="email-verification-error">
            <div className="error-icon">✕</div>
            <h1 className="email-verification-title">Verification Failed</h1>
            <p className="error-message">
              This verification link is invalid or has expired.
            </p>
            <p className="error-submessage">
              Please request a new email change from your settings page.
            </p>
            <a href="/welcome" className="email-verification-link">
              Back to Login
            </a>
          </div>
        </div>
      </div>
    );
  }

  if (verificationData) {
    return (
      <div className="email-verification-page">
        <div className="email-verification-container">
          <div className="email-verification-logo">
            <img src={AssetUrls.logos.iconLogo} alt="TradeMagic" />
          </div>

          <div className="email-verification-success">
            <div className="success-icon">✓</div>
            <h1 className="email-verification-title">Email Successfully Changed!</h1>

            {verificationData.email && (
              <p className="success-message">
                Your email has been updated to:
                <br />
                <strong className="new-email">{verificationData.email}</strong>
              </p>
            )}

            <p className="redirect-message">
              Redirecting to login in {countdown} seconds...
            </p>

            <p className="info-message">
              Please log in with your new email address.
            </p>

            <a href="/welcome" className="email-verification-link">
              Go to Login Now
            </a>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default EmailVerificationPage;